import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EyeIcon,
  PencilIcon,
  DocumentArrowDownIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  TrashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { adminService, Booking } from '../../services/adminService';
import { API_BASE_URL, authFetch } from '../../config/api.config';
import BookingEditDialog from './BookingEditDialog';
import './QuoteManagement.css';
import './BookingEditDialog.css';

// Map BookingStatus to German labels for quotes
// T-042 (11.09.2026, Kundin-Befund "Angebotsversand Mail nicht angekommen"): OfferSent wird schon
// beim Anlegen im Buchungsablauf gesetzt, die Angebots-Mail geht erst per "Per E-Mail senden" raus.
// Das Label "Versendet" war deshalb irrefuehrend, jetzt "Offen" plus eigene Versandzeile (quoteEmailSentAt).
const STATUS_LABELS: Record<string, string> = {
  Draft: 'Entwurf',
  OfferSent: 'Offen',
  OfferAccepted: 'Angenommen',
  OfferDeclined: 'Abgelehnt',
  OfferExpired: 'Abgelaufen',
  Confirmed: 'In Buchung umgewandelt',
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// T-042: Datum plus Uhrzeit fuer die Versandzeile der Angebots-Mail
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

// Check if a quote is expired based on QuoteValidUntil
const isQuoteExpired = (quoteValidUntil: string | null | undefined): boolean => {
  if (!quoteValidUntil) return false;
  return new Date(quoteValidUntil) < new Date();
};

// Format quote number from booking number
const formatQuoteNumber = (bookingNumber: string | null | undefined): string => {
  if (!bookingNumber) return '';
  if (bookingNumber.startsWith('ANG-')) return bookingNumber;
  if (bookingNumber.startsWith('FB-')) {
    return bookingNumber.replace('FB-', 'ANG-');
  }
  // Handle format like "B260082" -> "ANG-2026-0082"
  const match = bookingNumber.match(/^B(\d{2})(\d{4})$/);
  if (match) {
    const year = '20' + match[1];
    const num = match[2];
    return `ANG-${year}-${num}`;
  }
  return `ANG-${bookingNumber}`;
};

// QuoteBooking extends Booking - all needed fields are already in Booking interface
type QuoteBooking = Booking;

const QuoteManagement: React.FC = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<QuoteBooking[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<QuoteBooking | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const pageSize = 20;

  // Sorting state
  const [sortBy, setSortBy] = useState<string>('bookingNumber');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownId(null);
    };

    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdownId]);

  // Load data from Booking API with bookingType=Quote filter
  const loadQuotes = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await adminService.getBookings({
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        bookingType: 'Quote',
        page: currentPage,
        pageSize: pageSize,
      });

      // Map bookings to quote format
      const bookings = result.bookings || [];
      setQuotes(bookings as QuoteBooking[]);
      setTotalCount(result.total || 0);
      setTotalPages(result.pagination?.totalPages || Math.ceil((result.total || 0) / pageSize));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Laden der Angebote');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  // Sort quotes
  const sortedQuotes = [...quotes].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'bookingNumber':
        const numA = parseInt((a.bookingNumber || '').replace(/\D/g, '')) || 0;
        const numB = parseInt((b.bookingNumber || '').replace(/\D/g, '')) || 0;
        comparison = numA - numB;
        break;
      case 'customer':
        comparison = (a.customerName || '').localeCompare(b.customerName || '');
        break;
      case 'tripDate':
        comparison = new Date(a.tripDate || 0).getTime() - new Date(b.tripDate || 0).getTime();
        break;
      case 'tripType':
        comparison = (a.tourTypeName || '').localeCompare(b.tourTypeName || '');
        break;
      case 'participantCount':
        comparison = (a.participantCount || 0) - (b.participantCount || 0);
        break;
      case 'totalAmount':
        comparison = (a.totalPrice || a.totalAmount || 0) - (b.totalPrice || b.totalAmount || 0);
        break;
      case 'status':
        comparison = (a.status || '').localeCompare(b.status || '');
        break;
      case 'quoteValidUntil':
        comparison = new Date(a.quoteValidUntil || 0).getTime() - new Date(b.quoteValidUntil || 0).getTime();
        break;
      default:
        comparison = 0;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Handle column header click for sorting
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Get sort indicator for column header
  const getSortIndicator = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? ' ▲' : ' ▼';
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadQuotes();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleNewQuote = () => {
    navigate('/admin/booking-new?presetStatus=Angebot');
  };

  const handleViewDetails = (quote: QuoteBooking) => {
    navigate(`/admin/bookings?search=${quote.bookingNumber}`);
  };

  const handleEditQuote = (quote: QuoteBooking) => {
    setSelectedQuote(quote);
    setShowEditDialog(true);
  };

  // Handle save edited quote - Dialog handles API calls, just reload list
  const handleSaveEditedQuote = async (_updatedBooking: any) => {
    setShowEditDialog(false);
    setSelectedQuote(null);
    loadQuotes();
  };

  const handleDownloadPDF = async (quote: QuoteBooking) => {
    try {
      // T-009: authFetch setzt die Auth-Header und refresht bei 401 automatisch
      const response = await authFetch(`${API_BASE_URL}/document/offer/${quote.id}`, {});

      if (!response.ok) {
        throw new Error('PDF konnte nicht generiert werden');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `angebot-${formatQuoteNumber(quote.bookingNumber)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert('Fehler beim PDF-Download: ' + err.message);
    }
  };

  const handleSendEmail = async (quote: QuoteBooking) => {
    if (!window.confirm(`Angebot ${formatQuoteNumber(quote.bookingNumber)} per E-Mail an ${quote.customerEmail} senden?`)) {
      return;
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/document/offer/${quote.id}/send`, {
        method: 'POST'
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || data.error || 'E-Mail konnte nicht gesendet werden');
      }

      // T-042: Empfaenger aus der Backend-Antwort nennen, Liste zeigt danach die Versandzeile
      alert(data.message || `Angebot wurde per E-Mail an ${quote.customerEmail} versendet`);
      loadQuotes();
    } catch (err: any) {
      alert('Fehler beim E-Mail-Versand: ' + err.message);
    }
  };

  const handleConvertToBooking = async (quote: QuoteBooking) => {
    if (!window.confirm(`Angebot ${formatQuoteNumber(quote.bookingNumber)} in eine bestätigte Buchung umwandeln?`)) {
      return;
    }

    try {
      await adminService.updateBookingStatus(quote.id, 'Confirmed');
      loadQuotes();
    } catch (err: any) {
      alert('Fehler beim Umwandeln: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteQuote = async (quote: QuoteBooking) => {
    if (!window.confirm(`Angebot ${formatQuoteNumber(quote.bookingNumber)} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }

    try {
      await adminService.updateBookingStatus(quote.id, 'Cancelled');
      loadQuotes();
    } catch (err: any) {
      alert('Fehler beim Löschen: ' + (err.response?.data?.message || err.message));
    }
  };

  // Get status class for badge styling
  const getStatusClass = (status: string) => {
    const normalizedStatus = status?.toLowerCase() || '';
    switch (normalizedStatus) {
      case 'draft': return 'status-pending';
      case 'offersent': return 'status-offer-sent';
      case 'offeraccepted': return 'status-offer-accepted';
      case 'offerdeclined': return 'status-offer-declined';
      case 'offerexpired': return 'status-offer-expired';
      case 'confirmed': return 'status-confirmed';
      default: return '';
    }
  };

  // Calculate statistics from loaded data
  const stats = {
    total: totalCount,
    drafts: quotes.filter(q => q.status === 'Draft').length,
    sent: quotes.filter(q => q.status === 'OfferSent').length,
    accepted: quotes.filter(q => q.status === 'OfferAccepted').length,
  };

  return (
    <div className="quote-management">
      <div className="page-header">
        <h1>Angebote</h1>
        <button
          className="btn btn-primary"
          onClick={handleNewQuote}
        >
          + Neues Angebot
        </button>
      </div>

      {/* Statistics Summary */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Gesamt</div>
        </div>
        <div className="stat-card draft">
          <div className="stat-value">{stats.drafts}</div>
          <div className="stat-label">Entwürfe</div>
        </div>
        <div className="stat-card sent">
          <div className="stat-value">{stats.sent}</div>
          <div className="stat-label">Offen</div>
        </div>
        <div className="stat-card accepted">
          <div className="stat-value">{stats.accepted}</div>
          <div className="stat-label">Angenommen</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="quote-toolbar">
        <div className="search-group">
          <input
            type="text"
            placeholder="Suche (Nr., Name, E-Mail)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            className="search-input"
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="status-select"
          >
            <option value="">Alle Status</option>
            <option value="Draft">Entwurf</option>
            <option value="OfferSent">Offen</option>
            <option value="OfferAccepted">Angenommen</option>
            <option value="OfferDeclined">Abgelehnt</option>
            <option value="OfferExpired">Abgelaufen</option>
          </select>
          <button className="btn btn-secondary" onClick={handleSearch}>
            Suchen
          </button>
          <button className="btn btn-link" onClick={handleClearFilters}>
            Filter zurücksetzen
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="loading-state">
          <ArrowPathIcon className="w-8 h-8 animate-spin" style={{ margin: '0 auto', display: 'block' }} />
          <p>Lädt Angebote...</p>
        </div>
      ) : (
        <>
          {/* Quotes Table */}
          <div className="quotes-table-container">
            <table className="quotes-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('bookingNumber')}>
                    Nr.{getSortIndicator('bookingNumber')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('customer')}>
                    Kunde{getSortIndicator('customer')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('tripType')}>
                    Tour{getSortIndicator('tripType')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('tripDate')}>
                    Datum{getSortIndicator('tripDate')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('participantCount')}>
                    Personen{getSortIndicator('participantCount')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('totalAmount')}>
                    Betrag{getSortIndicator('totalAmount')}
                  </th>
                  <th className="sortable text-center" onClick={() => handleSort('status')}>
                    Status{getSortIndicator('status')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('quoteValidUntil')}>
                    Gültig bis{getSortIndicator('quoteValidUntil')}
                  </th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {sortedQuotes.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty-state">
                      Keine Angebote gefunden
                    </td>
                  </tr>
                ) : (
                  sortedQuotes.map((quote) => {
                    const expired = isQuoteExpired(quote.quoteValidUntil);
                    const quoteNumber = formatQuoteNumber(quote.bookingNumber);

                    return (
                      <tr key={quote.id} className={expired ? 'expired-row' : ''}>
                        <td>
                          <div className="number-info">
                            <span className="quote-number">{quoteNumber}</span>
                            <span className="booking-number">{quote.bookingNumber}</span>
                          </div>
                        </td>
                        <td>
                          <div className="customer-info">
                            <span className="customer-name">{quote.customerName || 'Unbekannt'}</span>
                            <span className="customer-email">{quote.customerEmail || ''}</span>
                          </div>
                        </td>
                        <td>
                          <div className="tour-info">
                            <span className="tour-type">{quote.tourTypeName || 'Floßfahrt'}</span>
                          </div>
                        </td>
                        <td>{formatDate(quote.tripDate)}</td>
                        <td className="text-center">{quote.participantCount}</td>
                        <td className="text-right">{formatCurrency(quote.totalPrice || quote.totalAmount || 0)}</td>
                        <td className="text-center">
                          <span className={`status-badge ${getStatusClass(quote.status)}`}>
                            {STATUS_LABELS[quote.status] || quote.status}
                          </span>
                          {/* T-042: Versandstand der Angebots-Mail (aus email_logs), unabhaengig vom Status */}
                          {(quote.status === 'OfferSent' || quote.status === 'Draft' || quote.status === 'OfferAccepted') && (
                            <div
                              className="quote-email-state"
                              style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: quote.quoteEmailSentAt ? '#15803d' : '#b45309' }}
                              title={quote.quoteEmailSentAt
                                ? `Angebots-Mail zuletzt versendet am ${formatDateTime(quote.quoteEmailSentAt)}`
                                : 'Die Angebots-Mail wurde noch nicht versendet. Aktionen, "Per E-Mail senden"'}
                            >
                              {quote.quoteEmailSentAt
                                ? `E-Mail ${formatDateTime(quote.quoteEmailSentAt)}`
                                : 'E-Mail noch nicht versendet'}
                            </div>
                          )}
                        </td>
                        <td className={expired ? 'expired-date' : ''}>
                          {formatDate(quote.quoteValidUntil)}
                          {expired && <span className="expired-label"> (abgelaufen)</span>}
                        </td>
                        <td className={openDropdownId === quote.id ? 'action-cell-open' : ''}>
                          <div className={`action-menu ${openDropdownId === quote.id ? 'open' : ''}`}>
                            <button
                              className="btn-action-menu"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdownId(openDropdownId === quote.id ? null : quote.id);
                              }}
                              title="Aktionen"
                            >
                              <span>...</span>
                            </button>
                            {openDropdownId === quote.id && (
                              <div className="action-dropdown">
                                {/* Ansicht in Buchungen */}
                                <button
                                  className="dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewDetails(quote);
                                    setOpenDropdownId(null);
                                  }}
                                >
                                  <EyeIcon className="w-4 h-4" />
                                  <span>In Buchungen anzeigen</span>
                                </button>

                                {/* Bearbeiten */}
                                <button
                                  className="dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditQuote(quote);
                                    setOpenDropdownId(null);
                                  }}
                                >
                                  <PencilIcon className="w-4 h-4" />
                                  <span>Bearbeiten</span>
                                </button>

                                {/* PDF Download */}
                                <button
                                  className="dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadPDF(quote);
                                    setOpenDropdownId(null);
                                  }}
                                >
                                  <DocumentArrowDownIcon className="w-4 h-4" />
                                  <span>PDF herunterladen</span>
                                </button>

                                {/* E-Mail senden */}
                                <button
                                  className="dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSendEmail(quote);
                                    setOpenDropdownId(null);
                                  }}
                                  disabled={!quote.customerEmail}
                                >
                                  <EnvelopeIcon className="w-4 h-4" />
                                  <span>Per E-Mail senden</span>
                                </button>

                                <div className="dropdown-divider"></div>

                                {/* In Buchung umwandeln */}
                                {(quote.status === 'OfferSent' || quote.status === 'OfferAccepted' || quote.status === 'Draft') && !expired && (
                                  <button
                                    className="dropdown-item dropdown-item-success"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleConvertToBooking(quote);
                                      setOpenDropdownId(null);
                                    }}
                                  >
                                    <CheckCircleIcon className="w-4 h-4" />
                                    <span>In Buchung umwandeln</span>
                                  </button>
                                )}

                                <div className="dropdown-divider"></div>

                                {/* Löschen */}
                                <button
                                  className="dropdown-item dropdown-item-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteQuote(quote);
                                    setOpenDropdownId(null);
                                  }}
                                >
                                  <TrashIcon className="w-4 h-4" />
                                  <span>Löschen</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(1)}
              >
                &laquo;
              </button>
              <button
                className="btn btn-secondary"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                &lsaquo;
              </button>
              <span className="page-info">
                Seite {currentPage} von {totalPages} ({totalCount} Angebote)
              </span>
              <button
                className="btn btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                &rsaquo;
              </button>
              <button
                className="btn btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(totalPages)}
              >
                &raquo;
              </button>
            </div>
          )}
        </>
      )}

      {/* Edit Dialog */}
      {selectedQuote && (
        <BookingEditDialog
          booking={{
            id: selectedQuote.id.toString(),
            bookingNumber: selectedQuote.bookingNumber || '',
            customerId: selectedQuote.customerId?.toString() || '',
            customerName: selectedQuote.customerName || '',
            customerEmail: selectedQuote.customerEmail || '',
            customerPhone: selectedQuote.customerPhone || '',
            tripDate: selectedQuote.tripDate || '',
            tripTime: selectedQuote.tripTime || '',
            tripType: selectedQuote.tourTypeName || selectedQuote.tripType || '',
            participantCount: selectedQuote.participantCount || 0,
            status: selectedQuote.status || 'Draft',
            paymentStatus: 'pending',
            totalAmount: selectedQuote.totalPrice || selectedQuote.totalAmount || 0,
            paidAmount: 0,
            notes: [],
            createdAt: selectedQuote.createdAt || '',
            updatedAt: selectedQuote.updatedAt || '',
            quoteValidUntil: selectedQuote.quoteValidUntil || ''
          }}
          isOpen={showEditDialog}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedQuote(null);
          }}
          onSave={handleSaveEditedQuote}
        />
      )}
    </div>
  );
};

export default QuoteManagement;
